import { useState, useEffect, useCallback } from 'react';
import { PageHeader, Card, LoadingSpinner } from '../components/Layout';
import { api } from '../services/api';

interface Bin {
  id: string;
  bin_number: string;
  location: string | null;
  workstation_type: string;
  order_id: string | null;
  work_order_id: string | null;
  order_number: string | null;
  status: 'available' | 'assigned' | 'in_use' | 'completed';
  assigned_at: string | null;
  assigned_by: string | null;
  created_at: string;
  updated_at: string;
}

interface BinStats {
  total: number;
  available: number;
  assigned: number;
  inUse: number;
  completed: number;
}

type StatusFilter = 'all' | 'available' | 'assigned' | 'in_use' | 'completed';

const STATUS_COLORS: Record<string, string> = {
  available: 'bg-green-100 text-green-800',
  assigned: 'bg-yellow-100 text-yellow-800',
  in_use: 'bg-blue-100 text-blue-800',
  completed: 'bg-gray-100 text-gray-800',
};

const WORKSTATION_TYPES = [
  { value: 'general', label: 'General' },
  { value: 'track', label: 'Track' },
  { value: 'housing', label: 'Housing' },
  { value: 'roller', label: 'Roller' },
  { value: 'fabric', label: 'Fabric' },
  { value: 'assembly', label: 'Assembly' },
  { value: 'qc', label: 'QC' },
  { value: 'shipping', label: 'Shipping' },
];

export default function BinsPage() {
  const [bins, setBins] = useState<Bin[]>([]);
  const [stats, setStats] = useState<BinStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [workstationFilter, setWorkstationFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [editingBin, setEditingBin] = useState<Bin | null>(null);
  const [toasts, setToasts] = useState<{ id: number; message: string; type: 'success' | 'error' }[]>([]);

  const addToast = (message: string, type: 'success' | 'error') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3000);
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [binsData, statsData] = await Promise.all([
        api.bins.getAll({
          status: statusFilter !== 'all' ? statusFilter : undefined,
          workstationType: workstationFilter || undefined,
        }),
        api.bins.getStats(),
      ]);
      setBins(binsData);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load bins:', error);
      addToast('Failed to load bins', 'error');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, workstationFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredBins = bins.filter(bin => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        bin.bin_number.toLowerCase().includes(search) ||
        bin.location?.toLowerCase().includes(search) ||
        bin.order_number?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const handleReleaseBin = async (bin: Bin) => {
    if (!confirm(`Release bin ${bin.bin_number}?`)) return;
    try {
      await api.bins.release(bin.id);
      addToast(`Bin ${bin.bin_number} released`, 'success');
      loadData();
    } catch (error) {
      addToast('Failed to release bin', 'error');
    }
  };

  const handleDeleteBin = async (bin: Bin) => {
    if (!confirm(`Delete bin ${bin.bin_number}? This cannot be undone.`)) return;
    try {
      await api.bins.delete(bin.id);
      addToast(`Bin ${bin.bin_number} deleted`, 'success');
      loadData();
    } catch (error: any) {
      addToast(error.message || 'Failed to delete bin', 'error');
    }
  };

  const handlePrintLabel = async (bin: Bin) => {
    try {
      const result = await api.bins.printLabel(bin.id);
      // Open a new window with the ZPL content for printing
      const printWindow = window.open('', '_blank', 'width=400,height=300');
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head><title>Print Label - ${result.binNumber}</title></head>
            <body style="font-family: monospace; padding: 20px;">
              <h3>ZPL Label for ${result.binNumber}</h3>
              <p>Send this ZPL code to your Zebra printer:</p>
              <textarea style="width: 100%; height: 150px; font-size: 12px;">${result.zpl}</textarea>
              <br/><br/>
              <button onclick="navigator.clipboard.writeText(document.querySelector('textarea').value); alert('Copied!')">
                Copy ZPL
              </button>
              <p style="margin-top: 20px; font-size: 12px; color: #666;">
                To print: Copy ZPL and send to printer via network or USB.
              </p>
            </body>
          </html>
        `);
        printWindow.document.close();
      }
      addToast(`Label generated for ${bin.bin_number}`, 'success');
    } catch (error: any) {
      addToast(error.message || 'Failed to print label', 'error');
    }
  };

  return (
    <div>
      <PageHeader
        title="Bin Management"
        subtitle="Manage production bins for work orders"
        actions={
          <div className="flex gap-2">
            <button
              onClick={async () => {
                try {
                  const needLabels = await api.bins.getBinsNeedingLabels();
                  if (needLabels.length === 0) {
                    addToast('All bins have labels printed', 'success');
                    return;
                  }
                  const binIds = needLabels.slice(0, 20).map((b: any) => b.id);
                  const result = await api.bins.printLabelsBatch(binIds);
                  // Create printable content
                  const printWindow = window.open('', '_blank');
                  if (printWindow) {
                    printWindow.document.write(`
                      <html>
                        <head><title>Batch Labels (${result.count} bins)</title></head>
                        <body style="font-family: monospace; padding: 20px;">
                          <h3>ZPL Labels for ${result.count} Bins</h3>
                          <textarea style="width: 100%; height: 400px; font-size: 11px;">${result.labels.map((l: any) => l.zpl).join('\n\n')}</textarea>
                          <br/><br/>
                          <button onclick="navigator.clipboard.writeText(document.querySelector('textarea').value); alert('Copied!')">Copy All ZPL</button>
                        </body>
                      </html>
                    `);
                    printWindow.document.close();
                  }
                  addToast(`Generated ${result.count} labels`, 'success');
                } catch (error: any) {
                  addToast(error.message || 'Failed to print labels', 'error');
                }
              }}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              Print Labels
            </button>
            <button
              onClick={() => setShowBulkModal(true)}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              + Bulk Create
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              + Add Bin
            </button>
          </div>
        }
      />

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card
            className={`text-center cursor-pointer transition-all ${statusFilter === 'all' ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
            <p className="text-sm text-gray-500">Total Bins</p>
          </Card>
          <Card
            className={`text-center cursor-pointer transition-all ${statusFilter === 'available' ? 'ring-2 ring-green-500' : ''}`}
            onClick={() => setStatusFilter('available')}
          >
            <p className="text-3xl font-bold text-green-600">{stats.available}</p>
            <p className="text-sm text-gray-500">Available</p>
          </Card>
          <Card
            className={`text-center cursor-pointer transition-all ${statusFilter === 'assigned' ? 'ring-2 ring-yellow-500' : ''}`}
            onClick={() => setStatusFilter('assigned')}
          >
            <p className="text-3xl font-bold text-yellow-600">{stats.assigned}</p>
            <p className="text-sm text-gray-500">Assigned</p>
          </Card>
          <Card
            className={`text-center cursor-pointer transition-all ${statusFilter === 'in_use' ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => setStatusFilter('in_use')}
          >
            <p className="text-3xl font-bold text-blue-600">{stats.inUse}</p>
            <p className="text-sm text-gray-500">In Use</p>
          </Card>
          <Card
            className={`text-center cursor-pointer transition-all ${statusFilter === 'completed' ? 'ring-2 ring-gray-500' : ''}`}
            onClick={() => setStatusFilter('completed')}
          >
            <p className="text-3xl font-bold text-gray-600">{stats.completed}</p>
            <p className="text-sm text-gray-500">Completed</p>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Bin number, location, or order..."
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="w-48">
            <label className="block text-sm font-medium text-gray-700 mb-1">Workstation Type</label>
            <select
              value={workstationFilter}
              onChange={e => setWorkstationFilter(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              {WORKSTATION_TYPES.map(wt => (
                <option key={wt.value} value={wt.value}>{wt.label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setStatusFilter('all');
                setWorkstationFilter('');
                setSearchTerm('');
              }}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </Card>

      {/* Bins Table */}
      <Card>
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : filteredBins.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {bins.length === 0 ? 'No bins found. Create some to get started.' : 'No bins match your filters.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bin #</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nickname</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Workstation</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned Order</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned At</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredBins.map((bin, idx) => (
                  <tr key={bin.id} className={idx % 2 === 0 ? '' : 'bg-gray-50'}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{bin.bin_number}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 font-medium">{bin.nickname || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{bin.location || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 capitalize">{bin.workstation_type}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[bin.status]}`}>
                        {bin.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {bin.order_number ? (
                        <a href={`/orders/${bin.order_id}`} className="text-blue-600 hover:underline">
                          {bin.order_number}
                        </a>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {bin.assigned_at ? new Date(bin.assigned_at).toLocaleString() : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handlePrintLabel(bin)}
                          className="text-purple-600 hover:text-purple-800"
                          title="Print Label"
                        >
                          Print
                        </button>
                        <button
                          onClick={() => setEditingBin(bin)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Edit"
                        >
                          Edit
                        </button>
                        {bin.status !== 'available' && (
                          <button
                            onClick={() => handleReleaseBin(bin)}
                            className="text-yellow-600 hover:text-yellow-800"
                            title="Release"
                          >
                            Release
                          </button>
                        )}
                        {bin.status === 'available' && (
                          <button
                            onClick={() => handleDeleteBin(bin)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Create Single Bin Modal */}
      {showCreateModal && (
        <CreateBinModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            loadData();
            addToast('Bin created', 'success');
          }}
        />
      )}

      {/* Bulk Create Modal */}
      {showBulkModal && (
        <BulkCreateModal
          onClose={() => setShowBulkModal(false)}
          onCreated={(count) => {
            setShowBulkModal(false);
            loadData();
            addToast(`${count} bins created`, 'success');
          }}
        />
      )}

      {/* Edit Bin Modal */}
      {editingBin && (
        <EditBinModal
          bin={editingBin}
          onClose={() => setEditingBin(null)}
          onSaved={() => {
            setEditingBin(null);
            loadData();
            addToast('Bin updated', 'success');
          }}
        />
      )}

      {/* Toast Notifications */}
      <div className="fixed bottom-4 right-4 z-50 space-y-2">
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={`px-4 py-3 rounded-lg shadow-lg text-white ${
              toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
            }`}
          >
            {toast.message}
          </div>
        ))}
      </div>
    </div>
  );
}

// Create Single Bin Modal
function CreateBinModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [binNumber, setBinNumber] = useState('');
  const [nickname, setNickname] = useState('');
  const [location, setLocation] = useState('');
  const [workstationType, setWorkstationType] = useState('general');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!binNumber.trim()) {
      setError('Bin number is required');
      return;
    }

    setSaving(true);
    setError('');
    try {
      await api.bins.create({
        binNumber: binNumber.trim(),
        nickname: nickname.trim() || undefined,
        location: location.trim() || undefined,
        workstationType,
      });
      onCreated();
    } catch (err: any) {
      setError(err.message || 'Failed to create bin');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Add New Bin</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bin Number *</label>
            <input
              type="text"
              value={binNumber}
              onChange={e => setBinNumber(e.target.value)}
              placeholder="e.g., BIN-051"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nickname</label>
            <input
              type="text"
              value={nickname}
              onChange={e => setNickname(e.target.value)}
              placeholder="e.g., Fabric Rack A, Extrusion Bay 3"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">Friendly name shown on label for easy identification</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <input
              type="text"
              value={location}
              onChange={e => setLocation(e.target.value)}
              placeholder="e.g., Rack 6"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Workstation Type</label>
            <select
              value={workstationType}
              onChange={e => setWorkstationType(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {WORKSTATION_TYPES.map(wt => (
                <option key={wt.value} value={wt.value}>{wt.label}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Creating...' : 'Create Bin'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Bulk Create Modal
function BulkCreateModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (count: number) => void;
}) {
  const [prefix, setPrefix] = useState('BIN');
  const [startNumber, setStartNumber] = useState('51');
  const [count, setCount] = useState('10');
  const [location, setLocation] = useState('');
  const [workstationType, setWorkstationType] = useState('general');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const previewBins = () => {
    const start = parseInt(startNumber) || 1;
    const num = Math.min(parseInt(count) || 1, 5);
    const bins = [];
    for (let i = 0; i < num; i++) {
      bins.push(`${prefix}-${(start + i).toString().padStart(3, '0')}`);
    }
    if ((parseInt(count) || 1) > 5) {
      bins.push('...');
    }
    return bins;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const countNum = parseInt(count) || 0;
    if (!prefix.trim() || countNum < 1) {
      setError('Prefix and count are required');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const created = await api.bins.bulkCreate({
        prefix: prefix.trim(),
        startNumber: parseInt(startNumber) || 1,
        count: countNum,
        location: location.trim() || undefined,
        workstationType,
      });
      onCreated(created.length);
    } catch (err: any) {
      setError(err.message || 'Failed to create bins');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Bulk Create Bins</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prefix *</label>
              <input
                type="text"
                value={prefix}
                onChange={e => setPrefix(e.target.value)}
                placeholder="BIN"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start #</label>
              <input
                type="number"
                value={startNumber}
                onChange={e => setStartNumber(e.target.value)}
                onWheel={(e) => e.currentTarget.blur()}
                min="1"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Count *</label>
              <input
                type="number"
                value={count}
                onChange={e => setCount(e.target.value)}
                onWheel={(e) => e.currentTarget.blur()}
                min="1"
                max="100"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <input
              type="text"
              value={location}
              onChange={e => setLocation(e.target.value)}
              placeholder="e.g., Rack 6"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Workstation Type</label>
            <select
              value={workstationType}
              onChange={e => setWorkstationType(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {WORKSTATION_TYPES.map(wt => (
                <option key={wt.value} value={wt.value}>{wt.label}</option>
              ))}
            </select>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-700 mb-1">Preview:</p>
            <p className="text-sm text-gray-600 font-mono">{previewBins().join(', ')}</p>
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Creating...' : `Create ${count} Bins`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Edit Bin Modal
function EditBinModal({
  bin,
  onClose,
  onSaved,
}: {
  bin: Bin;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [nickname, setNickname] = useState(bin.nickname || '');
  const [location, setLocation] = useState(bin.location || '');
  const [workstationType, setWorkstationType] = useState(bin.workstation_type);
  const [status, setStatus] = useState(bin.status);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.bins.update(bin.id, {
        nickname: nickname.trim() || undefined,
        location: location.trim() || undefined,
        workstationType,
        status,
      });
      onSaved();
    } catch (err: any) {
      setError(err.message || 'Failed to update bin');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Edit Bin: {bin.bin_number}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bin Number</label>
            <input
              type="text"
              value={bin.bin_number}
              disabled
              className="w-full px-3 py-2 border rounded-lg bg-gray-100 text-gray-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nickname</label>
            <input
              type="text"
              value={nickname}
              onChange={e => setNickname(e.target.value)}
              placeholder="e.g., Fabric Rack A"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">Shown on label for easy identification</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <input
              type="text"
              value={location}
              onChange={e => setLocation(e.target.value)}
              placeholder="e.g., Rack 6"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Workstation Type</label>
            <select
              value={workstationType}
              onChange={e => setWorkstationType(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {WORKSTATION_TYPES.map(wt => (
                <option key={wt.value} value={wt.value}>{wt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={status}
              onChange={e => setStatus(e.target.value as Bin['status'])}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="available">Available</option>
              <option value="assigned">Assigned</option>
              <option value="in_use">In Use</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          {bin.order_number && (
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                <span className="font-medium">Assigned to:</span> {bin.order_number}
              </p>
              {bin.assigned_at && (
                <p className="text-sm text-blue-600">
                  Since: {new Date(bin.assigned_at).toLocaleString()}
                </p>
              )}
            </div>
          )}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
